import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Avatar,
  Alert, CircularProgress, ToggleButtonGroup, ToggleButton,
  Card, CardContent, Divider, Tooltip, TextField, InputAdornment,
  Badge, Snackbar,
} from '@mui/material';
import {
  Cake, WorkspacePremium, Today, CalendarMonth,
  Search, ContentCopy, FilterList,
} from '@mui/icons-material';
import { apiFetch } from '../../services/apiFetch';

interface MilestoneEvent {
  type: 'birthday' | 'anniversary';
  employeeId: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  department?: string;
  position?: string;
  dateThisYear: string;
  daysFromToday: number;
  years?: number;
  originalDate: string;
}

interface Stats {
  today: number;
  next7: number;
  next30: number;
  next90: number;
}

function getInitials(first: string, last: string) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

function avatarColor(name: string) {
  const colors = ['#1976d2','#7b1fa2','#388e3c','#f57c00','#d32f2f','#0288d1','#5d4037','#00796b'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-HK', { weekday: 'short', month: 'short', day: 'numeric' });
}

function daysLabel(days: number) {
  if (days === 0) return { label: 'Today! 🎉', color: 'error' as const };
  if (days === 1) return { label: 'Tomorrow', color: 'warning' as const };
  if (days <= 7) return { label: `In ${days} days`, color: 'warning' as const };
  return { label: `In ${days} days`, color: 'default' as const };
}

function ordinal(n: number) {
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

// Group events by week bucket
function groupByBucket(events: MilestoneEvent[]) {
  const buckets: Record<string, MilestoneEvent[]> = {
    today:   [],
    this_week: [],
    next_week: [],
    this_month: [],
    later: [],
  };
  for (const e of events) {
    if (e.daysFromToday === 0)       buckets.today.push(e);
    else if (e.daysFromToday <= 7)   buckets.this_week.push(e);
    else if (e.daysFromToday <= 14)  buckets.next_week.push(e);
    else if (e.daysFromToday <= 30)  buckets.this_month.push(e);
    else                             buckets.later.push(e);
  }
  return buckets;
}

const BUCKET_LABELS: Record<string, string> = {
  today:      '🎉 Today',
  this_week:  '📅 This Week',
  next_week:  '📅 Next Week',
  this_month: '🗓️ This Month',
  later:      '⏳ Later',
};

const Milestones: React.FC = () => {
  const [events, setEvents]     = useState<MilestoneEvent[]>([]);
  const [stats, setStats]       = useState<Stats | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [window_, setWindow]    = useState<30 | 60 | 90 | 180>(90);
  const [typeFilter, setTypeFilter] = useState<'all' | 'birthday' | 'anniversary'>('all');
  const [search, setSearch]     = useState('');
  const [snack, setSnack]       = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [evRes, stRes] = await Promise.all([
        apiFetch(`/api/employees/milestones?days=${window_}&type=${typeFilter}`),
        apiFetch('/api/employees/milestones/stats'),
      ]);
      if (!evRes.ok || !stRes.ok) throw new Error('Failed to load');
      const [ev, st] = await Promise.all([evRes.json(), stRes.json()]);
      setEvents(ev);
      setStats(st);
    } catch (err: any) {
      setError(err.message || 'Failed to load milestones');
    } finally {
      setLoading(false);
    }
  }, [window_, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredEvents = events.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      e.firstName.toLowerCase().includes(q) ||
      e.lastName.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q) ||
      e.position?.toLowerCase().includes(q)
    );
  });

  const buckets = groupByBucket(filteredEvents);

  const copyList = () => {
    const lines = filteredEvents.map((e) => {
      const tag = e.type === 'birthday' ? '🎂' : '🏆';
      const years = e.years ? ` (${ordinal(e.years)} ${e.type === 'birthday' ? 'birthday' : 'anniversary'})` : '';
      return `${tag} ${e.firstName} ${e.lastName}${years} — ${formatDate(e.dateThisYear)} (${e.daysFromToday === 0 ? 'Today' : `in ${e.daysFromToday} days`})`;
    });
    navigator.clipboard.writeText(lines.join('\n'));
    setSnack('Copied to clipboard!');
  };

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Birthdays & Anniversaries</Typography>
          <Typography variant="body2" color="text.secondary">Upcoming milestones for active employees</Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<ContentCopy />} onClick={copyList} disabled={filteredEvents.length === 0}>
          Copy List
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats row */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Today',   value: stats.today,  icon: '🎉', color: '#d32f2f' },
            { label: 'Next 7d', value: stats.next7,  icon: '📅', color: '#f57c00' },
            { label: 'Next 30d',value: stats.next30, icon: '🗓️', color: '#1976d2' },
            { label: 'Next 90d',value: stats.next90, icon: '📆', color: '#388e3c' },
          ].map(({ label, value, icon, color }) => (
            <Grid key={label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', border: value > 0 ? `2px solid ${color}20` : undefined }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography fontSize={22}>{icon}</Typography>
                  <Typography variant="h4" fontWeight={700} color={value > 0 ? color : 'text.disabled'}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <ToggleButtonGroup
          value={typeFilter}
          exclusive
          onChange={(_, v) => v && setTypeFilter(v)}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="birthday"><Cake sx={{ mr: 0.5, fontSize: 16 }} />Birthdays</ToggleButton>
          <ToggleButton value="anniversary"><WorkspacePremium sx={{ mr: 0.5, fontSize: 16 }} />Anniversaries</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={window_}
          exclusive
          onChange={(_, v) => v && setWindow(v)}
          size="small"
        >
          {([30, 60, 90, 180] as const).map((d) => (
            <ToggleButton key={d} value={d}>{d}d</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search name / dept…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 200 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredEvents.length} upcoming
        </Typography>
      </Box>

      {/* Events grouped by bucket */}
      {filteredEvents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">🎈 No upcoming milestones in this window</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>Try expanding the look-ahead window or check that employees have date of birth / start date set.</Typography>
        </Paper>
      ) : (
        Object.entries(buckets)
          .filter(([, evs]) => evs.length > 0)
          .map(([bucket, evs]) => (
            <Box key={bucket} mb={3}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" mb={1}>
                {BUCKET_LABELS[bucket]}
              </Typography>
              <Grid container spacing={2}>
                {evs.map((ev) => {
                  const { label, color } = daysLabel(ev.daysFromToday);
                  const isBirthday = ev.type === 'birthday';
                  return (
                    <Grid key={`${ev.employeeId}-${ev.type}`} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                          borderLeft: `4px solid ${isBirthday ? '#e91e63' : '#ff9800'}`,
                          bgcolor: ev.daysFromToday === 0 ? 'warning.50' : 'background.paper',
                          transition: 'box-shadow 0.2s',
                          '&:hover': { boxShadow: 3 },
                        }}
                      >
                        <Badge
                          badgeContent={isBirthday ? '🎂' : '🏆'}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          sx={{ '& .MuiBadge-badge': { fontSize: 14, bgcolor: 'transparent', p: 0, minWidth: 'auto', right: -2, bottom: -2 } }}
                        >
                          <Avatar sx={{ bgcolor: avatarColor(`${ev.firstName}${ev.lastName}`), width: 48, height: 48, fontWeight: 700 }}>
                            {getInitials(ev.firstName, ev.lastName)}
                          </Avatar>
                        </Badge>

                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {ev.firstName} {ev.lastName}
                            {ev.nickname ? ` "${ev.nickname}"` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {ev.position || ev.department || '—'}
                            {ev.department && ev.position ? ` · ${ev.department}` : ''}
                          </Typography>
                          <Box display="flex" gap={0.75} mt={0.5} flexWrap="wrap">
                            <Chip label={label} color={color} size="small" />
                            {ev.years && (
                              <Chip
                                label={isBirthday ? `Age ${ev.years}` : `${ordinal(ev.years)} year`}
                                size="small"
                                variant="outlined"
                                color={isBirthday ? 'secondary' : 'warning'}
                              />
                            )}
                          </Box>
                        </Box>

                        <Box textAlign="right" flexShrink={0}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {isBirthday ? <Cake fontSize="inherit" /> : <WorkspacePremium fontSize="inherit" />}
                            {' '}{formatDate(ev.dateThisYear)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Milestones;
